import { useMemo } from "react";
import { Box, Paper, Skeleton, Tooltip, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useCentralAlertas } from "../context";
import {
	LABEL_SEVERIDADE,
	SEVERIDADES,
	formatarDuracaoCurta,
} from "../../../utils/centralDeAlertas/labels";
import {
	corSeveridade,
	corStatusIncidente,
} from "../../../utils/centralDeAlertas/cores";
import useStyles from "./styles";

// Faixa de resumo do topo: total do período, distribuição por severidade
// (barra empilhada), incidentes em aberto e MTTR — leitura em 3 segundos.
export default function ResumoIncidentes() {
	const theme = useTheme();
	const styles = useStyles(theme);
	const { carregando, incidentesFiltrados, intervalo } = useCentralAlertas();

	const resumo = useMemo(() => {
		const porSeveridade = SEVERIDADES.map((severidade) => ({
			severidade,
			total: incidentesFiltrados.filter((i) => i.severidade === severidade)
				.length,
		})).filter((item) => item.total > 0);
		const mttrs = incidentesFiltrados
			.map((i) => i.mttr)
			.filter((valor) => valor !== null && valor !== undefined);
		return {
			total: incidentesFiltrados.length,
			abertos: incidentesFiltrados.filter((i) => i.aberto).length,
			porSeveridade,
			mttr: mttrs.length
				? mttrs.reduce((soma, valor) => soma + valor, 0) / mttrs.length
				: null,
		};
	}, [incidentesFiltrados]);

	if (carregando) {
		return <Skeleton variant="rounded" sx={styles.skeleton} />;
	}

	const dias = Math.max(1, Math.round(intervalo.fim.diff(intervalo.inicio, "day", true)));
	const corAberto = corStatusIncidente(theme, "aberto");
	const corMttr = corStatusIncidente(theme, "resolvido");

	return (
		<Paper sx={styles.container}>
			<Box sx={styles.total}>
				<Typography sx={styles.numeroTotal}>{resumo.total}</Typography>
				<Typography variant="caption" sx={styles.rotulo}>
					{`incidentes em ${dias} ${dias === 1 ? "dia" : "dias"}`}
				</Typography>
			</Box>
			<Box sx={styles.barra}>
				{resumo.porSeveridade.map(({ severidade, total }) => (
					<Tooltip
						key={severidade}
						title={`${LABEL_SEVERIDADE[severidade]}: ${total}`}
						arrow
					>
						<Box sx={styles.segmento(corSeveridade(theme, severidade), total)} />
					</Tooltip>
				))}
			</Box>
			<Box sx={styles.contadores}>
				{resumo.porSeveridade.map(({ severidade, total }) => (
					<Box key={severidade} sx={styles.contador}>
						<Box sx={styles.ponto(corSeveridade(theme, severidade))} />
						<Typography sx={styles.valor(corSeveridade(theme, severidade))}>
							{total}
						</Typography>
						<Typography variant="caption" sx={styles.rotulo}>
							{LABEL_SEVERIDADE[severidade].toLowerCase()}
						</Typography>
					</Box>
				))}
				<Box sx={styles.separador} />
				<Box sx={styles.contador}>
					<Typography sx={styles.valor(resumo.abertos ? corAberto : null)}>
						{resumo.abertos}
					</Typography>
					<Typography variant="caption" sx={styles.rotulo}>
						em aberto
					</Typography>
				</Box>
				<Box sx={styles.contador}>
					<Typography sx={styles.valor(corMttr)}>
						{formatarDuracaoCurta(resumo.mttr)}
					</Typography>
					<Typography variant="caption" sx={styles.rotulo}>
						MTTR
					</Typography>
				</Box>
			</Box>
		</Paper>
	);
}
